import Overlay from 'ol/Overlay.js';
import { unByKey } from 'ol/Observable.js';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style.js';

const PRIMARY_COLOR = '#3b39e8';
const PRIMARY_FILL_COLOR = 'rgba(59, 57, 232, 0.18)';

const UNIT_LABELS = {
  degrees: '\u00b0',
  ft: 'ft',
  m: 'm',
  'us-ft': 'US ft'
};

function formatRadius(radius, unit) {
  const value = String(Math.round(radius));
  const unitLabel = UNIT_LABELS[unit] || unit || '';
  return `R = ${value}${unitLabel ? ` ${unitLabel}` : ''}`;
}

export function createRadiusDrawStyle() {
  return new Style({
    fill: new Fill({ color: PRIMARY_FILL_COLOR }),
    stroke: new Stroke({ color: PRIMARY_COLOR, width: 3 }),
    image: new CircleStyle({
      radius: 6,
      fill: new Fill({ color: PRIMARY_COLOR }),
      stroke: new Stroke({ color: '#ffffff', width: 2 })
    })
  });
}

function createLabelElement() {
  const element = document.createElement('div');
  element.className = 'radius-draw-label';
  element.setAttribute('role', 'status');
  Object.assign(element.style, {
    background: PRIMARY_COLOR,
    border: `1px solid ${PRIMARY_COLOR}`,
    borderRadius: '4px',
    color: '#ffffff',
    font: '600 13px sans-serif',
    padding: '3px 6px',
    pointerEvents: 'none',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
    whiteSpace: 'nowrap'
  });
  return element;
}

/**
 * Displays a live radius label above the circle sketch canvas.
 * The radius and unit both come from the active map projection.
 */
export function attachRadiusDrawLabel(drawInteraction, map) {
  let geometryChangeKey = null;
  let pointerMoveKey = null;
  let overlay = null;

  const removeLabel = () => {
    if (geometryChangeKey) {
      unByKey(geometryChangeKey);
      geometryChangeKey = null;
    }
    if (pointerMoveKey) {
      unByKey(pointerMoveKey);
      pointerMoveKey = null;
    }
    if (overlay) {
      map.removeOverlay(overlay);
      overlay = null;
    }
  };

  drawInteraction.on('drawstart', event => {
    removeLabel();

    const circle = event.feature.getGeometry();
    const element = createLabelElement();
    overlay = new Overlay({
      element,
      offset: [12, 0],
      positioning: 'center-left',
      stopEvent: false
    });
    map.addOverlay(overlay);

    const updateLabel = () => {
      const radius = circle.getRadius();
      const unit = map.getView().getProjection().getUnits();
      element.textContent = formatRadius(radius, unit);
    };

    geometryChangeKey = circle.on('change', updateLabel);
    pointerMoveKey = map.on('pointermove', pointerEvent => {
      overlay?.setPosition(pointerEvent.coordinate);
    });
    updateLabel();
  });

  drawInteraction.on('drawend', removeLabel);
  drawInteraction.on('drawabort', removeLabel);
}
